import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  MapPinIcon,
  DocumentIcon,
  PaperClipIcon,
  CogIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  BriefcaseIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageModal from '../common/ImageModal';
import toast from 'react-hot-toast';
import { leadsAPI, leadStagesAPI, usersAPI, industriesAPI, companiesAPI, customersAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';

const LeadForm = ({ leadId, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadStages, setLeadStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const { companyData, isStorageLimitExceeded } = useCompany();

  const [formData, setFormData] = useState({
    // Customer Link
    customer: '',

    // Lead-specific data only
    source: 'Walk In',
    stage: '',
    priority: 'medium',
    value: 0,
    notes: '',
    tags: [],
    assignedTo: '',
    expectedCloseDate: '',
    nextFollowupDate: '',

    // Custom Fields (will be populated from company settings)
    customFields: {}
  });

  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [activeTab, setActiveTab] = useState('preview');

  useEffect(() => {
    if (companyData?.industry) {
      fetchInitialData();
    }
    if (leadId) {
      fetchLead();
    }
  }, [leadId, companyData?.industry]);

  const fetchInitialData = async () => {
    try {
      const [stagesResponse, usersResponse, companyFieldsResponse] = await Promise.all([
        leadStagesAPI.getAll(),
        leadsAPI.getAssignableUsers(),
        companiesAPI.getCustomFields('lead')
      ]);

      setLeadStages(stagesResponse.data.leadStages || []);
      setUsers(usersResponse.data.users || []);

      // Use only company custom fields for leads
      const companyFields = companyFieldsResponse.data.customFields || [];
      setCustomFields(companyFields);

      // Set default stage if creating new lead
      if (!leadId && stagesResponse.data.leadStages?.length > 0) {
        const defaultStage = stagesResponse.data.leadStages.find(stage => stage.isDefault) ||
                            stagesResponse.data.leadStages[0];
        setFormData(prev => ({ ...prev, stage: defaultStage._id }));
      }
    } catch (error) {
      console.error('Fetch initial data error:', error);
      toast.error('Failed to load form data');
    }
  };

  const fetchLead = async () => {
    try {
      setLoading(true);
      const response = await leadsAPI.getById(leadId);
      const lead = response.data;
      
      console.log('Fetched lead:', lead);
      console.log('Lead attachments:', lead.attachments);

      setFormData({
        customer: lead.customer?._id || '',
        source: lead.source || '',
        stage: lead.stage?._id || lead.stage || '',
        priority: lead.priority || 'medium',
        value: lead.value || 0,
        notes: lead.notes || '',
        tags: lead.tags || [],
        assignedTo: lead.assignedTo?._id || '',
  expectedCloseDate: lead.expectedCloseDate ? lead.expectedCloseDate.split('T')[0] : '',
  nextFollowupDate: lead.nextFollowupDate ? (lead.nextFollowupDate.length > 16 ? lead.nextFollowupDate.slice(0,16) : lead.nextFollowupDate) : '',
        customFields: lead.customFields || {}
      });

      // Set selected customer data for display
      setSelectedCustomer(lead.customer || null);

      setAttachments(lead.attachments || []);
      console.log('Set attachments:', lead.attachments || []);
    } catch (error) {
      console.error('Fetch lead error:', error);
      toast.error('Failed to load lead data');
    } finally {
      setLoading(false);
    }
  };

  // Custom debounce hook
  const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);

    const debouncedCallback = useCallback((...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);

    return debouncedCallback;
  };

  const searchCustomers = async (query) => {
    if (!query.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setIsSearchingCustomers(true);
      const response = await customersAPI.getAll({ search: query, limit: 10 });
      setCustomerSearchResults(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Debounced search function with 300ms delay
  const debouncedSearchCustomers = useDebounce(searchCustomers, 300);

  const handleCustomerSelect = async (customerId) => {
    if (!customerId) {
      // Clear customer link
      setFormData(prev => ({ ...prev, customer: '' }));
      setSelectedCustomer(null);
      return;
    }

    try {
      const response = await customersAPI.getById(customerId);
      const customer = response.data;

      // Set customer reference and store customer data for display
      setFormData(prev => ({ ...prev, customer: customerId }));
      setSelectedCustomer(customer);
      setCustomerSearch('');
      setCustomerSearchResults([]);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer data');
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    debouncedSearchCustomers(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Require customer selection
    if (!formData.customer) {
      toast.error('Please select a customer for this lead');
      return;
    }

    // Require lead value
    if (!formData.value || formData.value <= 0) {
      toast.error('Please enter a valid lead value');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        customerId: formData.customer,
        source: formData.source,
        stage: formData.stage,
        priority: formData.priority,
        value: formData.value,
        notes: formData.notes,
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        expectedCloseDate: formData.expectedCloseDate,
        nextFollowupDate: formData.nextFollowupDate,
        customFields: formData.customFields
      };

      // Only include assignedTo if it has a valid value
      if (formData.assignedTo && formData.assignedTo.trim() !== '') {
        submitData.assignedTo = formData.assignedTo;
      }

      if (leadId) {
        await leadsAPI.update(leadId, submitData);
        toast.success('Lead updated successfully');
      } else {
        await leadsAPI.create(submitData);
        toast.success('Lead created successfully');
      }

      onSuccess?.();
    } catch (error) {
      console.error('Submit lead error:', error);
      toast.error(error.response?.data?.message || 'Failed to save lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if storage limit is exceeded
    if (isStorageLimitExceeded()) {
      toast.error('Storage limit exceeded. Please delete some files or upgrade your plan before uploading.');
      e.target.value = '';
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file, file.name); // Include filename
    formDataUpload.append('category', 'other');

    try {
      setUploadingFile(true);
      await leadsAPI.uploadAttachment(leadId, formDataUpload);
      toast.success('File uploaded successfully');

      // Refresh attachments
      const attachmentsResponse = await leadsAPI.getAttachments(leadId);
      setAttachments(attachmentsResponse.data || []);
    } catch (error) {
      console.error('Upload file error:', error);
      
      // Handle quota-related errors with more user-friendly messages
      if (error.response?.status === 413 && error.response?.data?.quotaType === 'storage') {
        if (error.response.data.wouldExceed) {
          toast.error(`Cannot upload: ${error.response.data.message}`);
        } else if (error.response.data.exceeded) {
          toast.error(`Storage limit exceeded: ${error.response.data.message}`);
        } else {
          toast.error(error.response.data.message || 'Storage quota exceeded');
        }
      } else {
        toast.error(error.response?.data?.message || 'Failed to upload file');
      }
    } finally {
      setUploadingFile(false);
      // Reset the file input
      e.target.value = '';
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await leadsAPI.downloadAttachment(leadId, attachment.fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setDeletingAttachment(attachment);
    setShowDeleteAttachmentModal(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!deletingAttachment) return;

    try {
      await leadsAPI.deleteAttachment(leadId, deletingAttachment.fileId);
      toast.success('Attachment deleted successfully');
      setAttachments(prev => prev.filter(att => att.fileId !== deletingAttachment.fileId));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setShowDeleteAttachmentModal(false);
      setDeletingAttachment(null);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

    if (imageTypes.includes(extension)) return 'image';
    if (documentTypes.includes(extension)) return 'document';
    return 'other';
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return (
          <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8.5 2H15.5L19 5.5V22H5V2H8.5ZM15 3.5V7H18.5L15 3.5ZM7 4V20H17V9H13V4H7ZM9 12H11V18H9V12ZM13 10H15V18H13V10Z"/>
          </svg>
        );
      case 'doc':
      case 'docx':
        return (
          <svg className="h-8 w-8 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z"/>
          </svg>
        );
      case 'xls':
      case 'xlsx':
        return (
          <svg className="h-8 w-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z"/>
          </svg>
        );
      case 'ppt':
      case 'pptx':
        return (
          <svg className="h-8 w-8 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z"/>
          </svg>
        );
      case 'txt':
        return (
          <svg className="h-8 w-8 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z"/>
          </svg>
        );
      default:
        return <DocumentIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  // Helper component to fetch blob preview and render image with cleanup
  const BlobImage = ({ leadId, fileId, alt, className }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
      let mounted = true;
      let objectUrl;

      const fetchPreview = async () => {
        try {
          const response = await leadsAPI.previewAttachment(leadId, fileId);
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          objectUrl = window.URL.createObjectURL(blob);
          if (mounted) setSrc(objectUrl);
        } catch (err) {
          console.error('Preview fetch error:', err);
        }
      };

      if (leadId && fileId) fetchPreview();

      return () => {
        mounted = false;
        if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      };
    }, [leadId, fileId]);

    if (!src) return null;
    return <img src={src} alt={alt} className={className} />;
  };

  // Image modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState(null);
  const [modalFile, setModalFile] = useState(null);

  // Delete modal states
  const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  const addTag = () => {
    if (newTag && newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  // Function to detect if a field name looks like an amount field
  const isAmountField = (fieldName) => {
    if (!fieldName) return false;
    const amountKeywords = ['amount', 'price', 'cost', 'value', 'budget', 'fee', 'charge', 'payment', 'salary', 'wage', 'rate', 'total', 'sum', 'money'];
    const lowerFieldName = fieldName.toLowerCase();
    return amountKeywords.some(keyword => lowerFieldName.includes(keyword));
  };

  // Auto-populate lead value when amount-like custom fields change
  useEffect(() => {
    const amountFields = customFields.filter(field => isAmountField(field.fieldName));
    if (amountFields.length === 0) return;

    // Find the first amount field that has a value
    for (const field of amountFields) {
      const fieldName = field.fieldName;
      const fieldValue = formData.customFields?.[fieldName];

      if (fieldValue && (typeof fieldValue === 'number' || (typeof fieldValue === 'string' && !isNaN(parseFloat(fieldValue))))) {
        const numericValue = parseFloat(fieldValue);
        if (numericValue > 0 && numericValue !== formData.value) {
          setFormData(prev => ({
            ...prev,
            value: numericValue
          }));
          break; // Only set from the first valid amount field
        }
      }
    }
  }, [formData.customFields, customFields]);

  const renderCustomField = (field) => {
    const fieldName = field.fieldName || 'unknown_field';
    const value = formData.customFields?.[fieldName] || '';
    const handleChange = (newValue) => {
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [fieldName]: newValue
        }
      }));
    };

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <input
            type={field.fieldType === 'text' ? 'text' : field.fieldType}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
            required={field.isRequired}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            min={field.validation?.min}
            max={field.validation?.max}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
            required={field.isRequired}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            required={field.isRequired}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            rows={4}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm resize-none ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
            required={field.isRequired}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            required={field.isRequired}
          >
            <option value="">Select {(field.fieldName || 'field').toLowerCase()}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={value === 'true' || value === true}
              onChange={(e) => handleChange(e.target.checked.toString())}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">{field.placeholder || (field.fieldName || 'Field')}</span>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={field.disabled}
            className={`w-full px-4 py-3 sm:px-5 sm:py-4 border-2 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${
              field.disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-300'
            }`}
            placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
            required={field.isRequired}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" text="Loading lead data..." />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 px-4 lg:px-6">

        <form onSubmit={handleSubmit} className="space-y-8 pt-8">
        {/* Contact Information Card */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden mb-8">
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 via-cyan-400/30 to-teal-400/30"></div>
            <div className="relative flex items-center space-x-3 sm:space-x-4">
              <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">👥 Contact Information</h2>
                <p className="text-blue-100 text-xs sm:text-sm">Basic contact details and customer linking</p>
              </div>
              <span className="text-blue-200 text-xs sm:text-sm font-bold bg-white/20 px-2 py-1 sm:px-3 rounded-full border border-white/30">*</span>
            </div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Customer Link Section */}
            <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-blue-50 via-cyan-50 to-teal-50 rounded-2xl sm:rounded-3xl border-2 border-blue-200/50 shadow-inner">
              <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                  <label className="block text-sm font-bold text-blue-800">🔗 Link to Existing Customer</label>
                </div>
                <button
                  type="button"
                  onClick={() => window.open('/customers', '_blank')}
                  className="px-3 py-2 sm:px-4 sm:py-2 text-sm bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 w-full sm:w-auto"
                >
                  ➕ Add New Customer
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={customerSearch}
                  onChange={handleCustomerSearchChange}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/80 border-2 border-blue-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-lg focus:shadow-xl text-sm sm:text-base"
                  placeholder="🔍 Search customers by name, email, or phone..."
                />
                {isSearchingCustomers && (
                  <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-blue-600 border-t-transparent"></div>
                  </div>
                )}
                {customerSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white/95 border-2 border-blue-200 rounded-xl sm:rounded-2xl shadow-2xl max-h-64 overflow-y-auto backdrop-blur-xl">
                    <div
                      className="px-4 py-3 sm:px-6 sm:py-4 text-sm text-gray-500 cursor-pointer hover:bg-blue-50 border-b border-blue-100 rounded-t-xl sm:rounded-t-2xl transition-colors duration-200"
                      onClick={() => handleCustomerSelect('')}
                    >
                      ❌ Clear selection
                    </div>
                    {customerSearchResults.map((customer) => (
                      <div
                        key={customer._id}
                        className="px-4 py-3 sm:px-6 sm:py-4 cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 border-b border-blue-100 last:border-b-0 last:rounded-b-xl sm:last:rounded-b-2xl transition-all duration-200 group"
                        onClick={() => handleCustomerSelect(customer._id)}
                      >
                        <div className="font-bold text-gray-900 group-hover:text-blue-800">
                          {customer.firstName} {customer.lastName}
                        </div>
                        <div className="text-sm text-gray-500 group-hover:text-blue-600">📧 {customer.email}</div>
                        {customer.phone && (
                          <div className="text-sm text-gray-500 group-hover:text-blue-600">📞 {customer.phone}</div>
                        )}
                        {customer.companyName && (
                          <div className="text-sm text-gray-400 group-hover:text-cyan-600">🏢 {customer.companyName}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedCustomer && (
                <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl shadow-inner">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm font-bold text-green-800">✅ Customer linked - fields below are auto-populated from customer data</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.firstName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter first name"
                  required
                  disabled={!!selectedCustomer}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.lastName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter last name"
                  required
                  disabled={!!selectedCustomer}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={selectedCustomer?.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter email address"
                  required
                  disabled={!!selectedCustomer}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  Phone
                </label>
                <input
                  type="tel"
                  value={selectedCustomer?.phone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter phone number"
                  disabled={!!selectedCustomer}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  Secondary Phone
                </label>
                <input
                  type="tel"
                  value={selectedCustomer?.secondaryPhone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryPhone: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-purple-50/50 border-2 border-purple-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-purple-300/50 focus:border-purple-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter secondary phone"
                  disabled={!!selectedCustomer}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700 flex items-center">
                  Website
                </label>
                <input
                  type="url"
                  value={selectedCustomer?.website || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-pink-50/50 border-2 border-pink-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-pink-300/50 focus:border-pink-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="https://example.com"
                  disabled={!!selectedCustomer}
                />
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-10 pt-8 border-t-2 border-gradient-to-r from-blue-200 to-cyan-200">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 animate-pulse"></div>
                🌐 Social Media Profiles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    💼 LinkedIn
                  </label>
                  <input
                    type="url"
                    value={selectedCustomer?.socialMedia?.linkedin || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, linkedin: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="https://linkedin.com/in/username"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🐦 Twitter
                  </label>
                  <input
                    type="url"
                    value={selectedCustomer?.socialMedia?.twitter || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, twitter: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-sky-50/50 border-2 border-sky-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-sky-300/50 focus:border-sky-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="https://twitter.com/username"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📘 Facebook
                  </label>
                  <input
                    type="url"
                    value={selectedCustomer?.socialMedia?.facebook || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, facebook: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-300 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-400/50 focus:border-blue-500 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="https://facebook.com/username"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📷 Instagram
                  </label>
                  <input
                    type="url"
                    value={selectedCustomer?.socialMedia?.instagram || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, instagram: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-pink-50/50 border-2 border-pink-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-pink-300/50 focus:border-pink-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="https://instagram.com/username"
                    disabled={!!selectedCustomer}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Industry Specific Fields Card */}
          {customFields.length > 0 && (
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-4">
              <div className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500 px-4 py-3 sm:px-6 sm:py-4">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 via-violet-400/30 to-indigo-400/30"></div>
                <div className="relative flex items-center space-x-2 sm:space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                    <CogIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white">⚙️ Industry Specific Fields</h2>
                    <p className="text-purple-100 text-xs">Custom fields for your industry</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {customFields.map((field, index) => (
                    <div key={field._id} className="space-y-2">
                      <label className="block text-sm font-bold text-gray-700 flex items-center">
                        {field.fieldName || 'Field'}
                        {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {renderCustomField(field)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Address Information Card */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-4">
            <div className="relative overflow-hidden bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 px-4 py-3 sm:px-6 sm:py-4">
              <div className="absolute inset-0 bg-gradient-to-r from-green-400/30 via-emerald-400/30 to-teal-400/30"></div>
              <div className="relative flex items-center space-x-2 sm:space-x-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white">📍 Address Information</h2>
                  <p className="text-green-100 text-xs">Location and contact details</p>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="sm:col-span-2 space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏠 Street Address
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.address?.street || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter street address"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏙️ City
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.address?.city || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-emerald-50/50 border-2 border-emerald-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-emerald-300/50 focus:border-emerald-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter city"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🗺️ State/Province
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.address?.state || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, state: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter state/province"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📮 ZIP/Postal Code
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.address?.zipCode || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, zipCode: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter ZIP/postal code"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🌍 Country
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.address?.country || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, country: e.target.value }
                    }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter country"
                    disabled={!!selectedCustomer}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Professional Information Card */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-4">
            <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 px-4 py-3 sm:px-6 sm:py-4">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400/30 via-amber-400/30 to-yellow-400/30"></div>
              <div className="relative flex items-center space-x-2 sm:space-x-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                  <BriefcaseIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white">💼 Professional Information</h2>
                  <p className="text-orange-100 text-xs">Career and business details</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏢 Company Name
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.companyName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-orange-50/50 border-2 border-orange-200 rounded-2xl focus:ring-4 focus:ring-orange-300/50 focus:border-orange-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter company name"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    👔 Job Title
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.jobTitle || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-amber-50/50 border-2 border-amber-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-amber-300/50 focus:border-amber-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter job title"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏛️ Department
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.department || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-yellow-50/50 border-2 border-yellow-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-yellow-300/50 focus:border-yellow-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter department"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📊 Industry
                  </label>
                  <input
                    type="text"
                    value={selectedCustomer?.industry || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-red-50/50 border-2 border-red-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-red-300/50 focus:border-red-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter industry"
                    disabled={!!selectedCustomer}
                  />
                </div>
              </div>

              {/* Lead Details */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                  🎯 Lead Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📈 Lead Stage
                    </label>
                    <select
                      value={formData.stage}
                      onChange={(e) => setFormData(prev => ({ ...prev, stage: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-pink-50/50 border-2 border-pink-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-pink-300/50 focus:border-pink-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    >
                      <option value="">Select Stage</option>
                      {leadStages.map(stage => (
                        <option key={stage._id} value={stage._id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      ⚡ Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-purple-50/50 border-2 border-purple-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-purple-300/50 focus:border-purple-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      💰 Lead Value ({companyData?.currency?.symbol || '$'})
                      <span className="text-red-500 ml-1">*</span>
                      <QuestionMarkCircleIcon
                        className="h-4 w-4 ml-2 text-gray-400 hover:text-gray-600 cursor-help"
                        title="The monetary value of this lead. Custom fields with amount-related names (like 'budget', 'price', 'cost') will automatically populate this field when changed."
                      />
                    </label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-indigo-50/50 border-2 border-indigo-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-indigo-300/50 focus:border-indigo-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                      placeholder={`0.00 ${companyData?.currency?.code || 'USD'}`}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📡 Lead Source
                    </label>
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    >
                      <option value="">Select Source</option>
                       <option value="Walk In">Walk In</option>
                      <option value="Website">Website</option>
                      <option value="Referral">Referral</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Email Campaign">Email Campaign</option>
                      <option value="Cold Call">Cold Call</option>
                      <option value="Trade Show">Trade Show</option>
                      <option value="Direct Mail">Direct Mail</option>
                      <option value="Online Ads">Online Ads</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      👤 Assigned To
                    </label>
                    <select
                      value={formData.assignedTo}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    >
                      <option value="">Select User</option>
                      {users.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📅 Expected Close Date
                    </label>
                    <input
                      type="date"
                      value={formData.expectedCloseDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, expectedCloseDate: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📞 Next Follow-up (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.nextFollowupDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, nextFollowupDate: e.target.value }))}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-4 space-y-3">
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📝 Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-violet-50/50 border-2 border-violet-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-violet-300/50 focus:border-violet-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base resize-none"
                      placeholder="Enter any additional notes..."
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="mt-4 sm:mt-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3 flex items-center">
                    🏷️ Tags
                  </label>
                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200 font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(index)}
                          className="ml-1 sm:ml-2 text-orange-600 hover:text-orange-800 transition-colors duration-200"
                        >
                          <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-3 py-2 sm:px-4 sm:py-3 lg:px-5 lg:py-4 bg-gradient-to-r from-white to-orange-50/50 border-2 border-orange-200 rounded-lg sm:rounded-xl lg:rounded-2xl focus:ring-4 focus:ring-orange-300/50 focus:border-orange-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base lg:text-lg"
                      placeholder="Add a tag..."
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-3 py-2 sm:px-4 sm:py-3 lg:px-6 lg:py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg sm:rounded-xl lg:rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 text-xs sm:text-sm lg:text-base"
                    >
                      ➕ Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>



          {/* Documents & Attachments Card */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-4">
            <div className="relative overflow-hidden bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 px-4 py-3 sm:px-6 sm:py-4">
              <div className="absolute inset-0 bg-gradient-to-r from-red-400/30 via-rose-400/30 to-pink-400/30"></div>
              <div className="relative flex items-center space-x-2 sm:space-x-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                  <PaperClipIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white">📎 Documents & Attachments</h2>
                  <p className="text-red-100 text-xs">Upload and manage files</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Upload Area */}
                <div className="relative border-2 border-dashed border-red-300 rounded-2xl p-8 text-center bg-gradient-to-br from-red-50/50 to-rose-50/50 hover:from-red-100/50 hover:to-rose-100/50 transition-all duration-300 backdrop-blur-sm">
                  <div className="p-3 bg-white/60 rounded-xl backdrop-blur-sm border border-white/40 inline-block mb-4">
                    <PaperClipIcon className="h-10 w-10 text-red-500 mx-auto" />
                  </div>
                  <div className="space-y-2">
                    {!leadId ? (
                      <>
                        <p className="text-gray-700 font-semibold text-base">📝 File upload available after lead creation</p>
                        <p className="text-xs text-gray-600">Create the lead first, then you can upload documents</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-700 font-semibold text-base">📤 Drop files here or click to upload</p>
                        <p className="text-xs text-gray-600">Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 2MB)</p>
                      </>
                    )}
                  </div>
                  {leadId && (
                    <input
                      type="file"
                      name="file"
                      onChange={handleFileUpload}
                      onClick={(e) => e.stopPropagation()}
                      disabled={uploadingFile}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                  )}
                  {uploadingFile && (
                    <div className="mt-6">
                      <LoadingSpinner size="small" text="Uploading..." />
                    </div>
                  )}
                </div>

                {/* Attachments Tabs */}
                {attachments.length > 0 && (
                  <div className="mt-6">
                    {/* Tab Navigation */}
                    <div className="flex border-b border-red-200 mb-6">
                      <button
                        type='button'
                        onClick={() => setActiveTab('list')}
                        className={`px-6 py-3 font-bold text-sm border-b-3 transition-all duration-300 rounded-t-lg ${
                          activeTab === 'list'
                            ? 'border-red-500 text-red-600 bg-gradient-to-r from-red-50 to-rose-50'
                            : 'border-transparent text-gray-500 hover:text-red-600 hover:bg-red-50/50'
                        }`}
                      >
                        📋 List View
                      </button>
                      <button
                        type='button'
                        onClick={() => setActiveTab('preview')}
                        className={`px-6 py-3 font-bold text-sm border-b-3 transition-all duration-300 rounded-t-lg ${
                          activeTab === 'preview'
                            ? 'border-red-500 text-red-600 bg-gradient-to-r from-red-50 to-rose-50'
                            : 'border-transparent text-gray-500 hover:text-red-600 hover:bg-red-50/50'
                        }`}
                      >
                        👁️ Preview
                      </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'list' && (
                      <div className="space-y-3">
                        <h4 className="font-bold text-gray-800 flex items-center text-base">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                          📁 Uploaded Documents
                        </h4>
                        {attachments.map((attachment) => (
                          <div
                            key={attachment.fileId}
                            className="flex items-center justify-between p-4 bg-gradient-to-r from-white to-red-50/30 border-2 border-red-200 rounded-xl hover:shadow-lg transition-all duration-300 backdrop-blur-sm"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-red-100 rounded-lg border border-red-200">
                                {getFileIcon(attachment.originalName)}
                              </div>
                              <div>
                                <p className="font-bold text-gray-800 text-sm">{attachment.originalName}</p>
                                <p className="text-xs text-gray-600">
                                  {(attachment.size / 1024 / 1024).toFixed(2)} MB •
                                  📅 Uploaded {formatDate(attachment.createdAt || attachment.uploadedAt)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadAttachment(attachment)}
                                className="p-2 text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-300 font-semibold"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(attachment)}
                                className="p-2 text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-300 font-semibold"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeTab === 'preview' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {attachments.map((attachment) => {
                          const fileType = getFileType(attachment.originalName);
                          const isPreviewable = fileType === 'image' || fileType === 'document';

                          return (
                            <div key={attachment.fileId} className="relative group bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200">
                              <div className="aspect-square bg-gray-50 rounded-md mb-2 flex items-center justify-center overflow-hidden">
                                {fileType === 'image' ? (
                                  <div className="w-full h-full">
                                    <BlobImage
                                      leadId={leadId}
                                      fileId={attachment.fileId}
                                      alt={attachment.originalName}
                                      className="w-full h-full object-cover cursor-pointer"
                                    />
                                    <button
                                      type="button"
                                      className="absolute inset-0 w-full h-full bg-transparent hover:bg-black/10 transition-colors duration-200"
                                      onClick={async () => {
                                        try {
                                          const res = await leadsAPI.previewAttachment(leadId, attachment.fileId);
                                          const blob = new Blob([res.data], { type: res.headers['content-type'] || 'image/*' });
                                          const url = window.URL.createObjectURL(blob);
                                          setModalSrc(url);
                                          setModalFile(attachment);
                                          setModalOpen(true);
                                        } catch (err) {
                                          console.error('Error opening modal preview:', err);
                                          toast.error('Failed to load preview');
                                        }
                                      }}
                                      title="Click to preview image"
                                    />
                                  </div>
                                ) : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-pink-50 rounded-md border border-red-200">
                                    <div className="text-red-500 mb-2">
                                      {getFileIcon(attachment.originalName)}
                                    </div>
                                    <div className="text-center px-2">
                                      <p className="text-xs font-medium text-red-700 mb-1">PDF Document</p>
                                      <p className="text-xs text-red-600 mb-2">{formatFileSize(attachment.size)}</p>
                                      <button
                                        type='button'
                                        className="px-3 py-1 bg-red-500 text-white text-xs rounded-md hover:bg-red-600 transition-colors duration-200 shadow-sm"
                                        onClick={async () => {
                                          try {
                                            const res = await leadsAPI.previewAttachment(leadId, attachment.fileId);
                                            const blob = new Blob([res.data], { type: 'application/pdf' });
                                            const url = window.URL.createObjectURL(blob);
                                            window.open(url, '_blank');
                                            // Clean up the blob URL after a delay
                                            setTimeout(() => {
                                              window.URL.revokeObjectURL(url);
                                            }, 1000);
                                          } catch (err) {
                                            console.error('Error opening PDF preview:', err);
                                            toast.error('Failed to load PDF preview');
                                          }
                                        }}
                                        title="Click to preview PDF"
                                      >
                                        👁️ Preview PDF
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    {getFileIcon(attachment.originalName)}
                                  </div>
                                )}
                              </div>
                              <div className="text-center">
                                <p className="text-sm font-medium text-gray-900 truncate" title={attachment.originalName}>{attachment.originalName}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                                {isPreviewable && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    {fileType === 'image' ? '🖼️ Click to preview' : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? '📄 Click to preview' : ''}
                                  </p>
                                )}
                              </div>

                              {/* Action buttons - shown on hover */}
                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                {isPreviewable && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (fileType === 'image') {
                                        try {
                                          const res = await leadsAPI.previewAttachment(leadId, attachment.fileId);
                                          const blob = new Blob([res.data], { type: res.headers['content-type'] || 'image/*' });
                                          const url = window.URL.createObjectURL(blob);
                                          setModalSrc(url);
                                          setModalFile(attachment);
                                          setModalOpen(true);
                                        } catch (err) {
                                          console.error('Error opening modal preview:', err);
                                          toast.error('Failed to load preview');
                                        }
                                      } else if (fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf')) {
                                        try {
                                          const res = await leadsAPI.previewAttachment(leadId, attachment.fileId);
                                          const blob = new Blob([res.data], { type: 'application/pdf' });
                                          const url = window.URL.createObjectURL(blob);
                                          window.open(url, '_blank');
                                          setTimeout(() => {
                                            window.URL.revokeObjectURL(url);
                                          }, 1000);
                                        } catch (err) {
                                          console.error('Error opening PDF preview:', err);
                                          toast.error('Failed to load PDF preview');
                                        }
                                      }
                                    }}
                                    className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors duration-200"
                                    title={fileType === 'image' ? 'Preview Image' : 'Preview PDF'}
                                  >
                                    <EyeIcon className="h-3 w-3" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-200"
                                  title="Download"
                                >
                                  <ArrowDownTrayIcon className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachment(attachment)}
                                  className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
                                  title="Delete"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-6 mt-6">
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-gray-300 rounded-xl sm:rounded-2xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                ❌ Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl sm:rounded-2xl hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 transition-all duration-300 font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                {submitting ? '⏳ Saving...' : (leadId ? '✨ Update Lead' : '🚀 Create Lead')}
              </button>
            </div>
          </div>
      </form>
    </div>
    {modalOpen && (
      <ImageModal
        open={modalOpen}
        src={modalSrc}
        alt={modalFile?.originalName}
        filename={modalFile?.originalName}
        onClose={() => {
          setModalOpen(false);
          if (modalSrc) {
            window.URL.revokeObjectURL(modalSrc);
            setModalSrc(null);
          }
          setModalFile(null);
        }}
        onDownload={() => handleDownloadAttachment(modalFile)}
        onDelete={async () => {
          await handleDeleteAttachment(modalFile);
          setModalOpen(false);
        }}
      />
    )}

    {/* Delete Attachment Confirmation Modal */}
    <AnimatePresence>
      {showDeleteAttachmentModal && deletingAttachment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowDeleteAttachmentModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <PaperClipIcon className="w-8 h-8 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Delete Attachment
              </h3>

              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingAttachment?.originalName}"</span>?
                This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowDeleteAttachmentModal(false);
                    setDeletingAttachment(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={confirmDeleteAttachment}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                >
                  Delete
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    </>
  );
};

export default LeadForm;
